<template>
  <ValidationObserver v-slot="{ handleSubmit }" slim>
    <form @submit.prevent="handleSubmit(selectUpdateOrCreate)">
      <div class="modal-card" :data-cy-tag="cyTag">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ header }}</p>
        </header>
        <section class="modal-card-body">
          <BaseInput
            v-model.trim="orgName"
            rules="required|attribute"
            label="Organisation name *"
            placeholder="Organisation name"
            maxlength="255"
            data-cy-tag="organisation-name"
          />
          <BaseInput
            v-model="abn"
            label="ABN/ACN *"
            placeholder="ABN or ACN"
            rules="abnOrAcn|required"
            maxlength="14"
            data-cy-tag="abn-acn"
            :disabled="!!organisationEdits?.abn"
          />
          <AddressAutocomplete
            v-model="address"
            label="Business Address *"
            placeholder="Business Address"
            rules="address|required"
            maxlength="255"
            data-cy-tag="business-address"
          />
          <BField label="Entity Type *">
            <BaseSelect
              v-model="entityType"
              placeholder="Entity Type"
              rules="required"
              data-cy-tag="entity-type"
              :disabled="!!organisationEdits?.entityType"
              expanded
            >
              <option v-for="[key, value] in partyEntityTypeOptions" :key="key" :value="key">
                {{ value }}
              </option>
            </BaseSelect>
          </BField>
          <BaseSelect
            v-model="industryType"
            label="Industry *"
            placeholder="Select Industry"
            rules="required"
            data-cy-tag="industry"
            :disabled="!!organisationEdits?.industryType"
            expanded
          >
            <option v-for="[key, value] in industryOptions" :key="key" :value="key">{{ value }}</option>
          </BaseSelect>

          <section v-if="isReferredSectionVisible">
            <RadioYesNoInput v-model="isReferredToAssuro" label="Were you referred to Assuro by a 3rd party?" />
            <ValidationProvider v-if="isReferredToAssuro" v-slot="{ errors }" rules="required" slim>
              <BField label="Partner" :type="{ 'is-danger': errors[0] }" :message="errors">
                <BAutocomplete
                  v-model="searchByName"
                  placeholder="Select Partner"
                  icon="search"
                  icon-right="chevron-down"
                  field="name"
                  rules="required"
                  data-cy-tag="partner-input"
                  open-on-focus
                  clearable
                  :data="filteredEntities"
                  :loading="isLoading"
                  @select="option => (selectedEntity = option)"
                  :custom-class="sensitiveField()"
                >
                  <template #empty>No results found</template>
                  <template slot-scope="props">
                    <SensitiveField>{{ props.option.name }}</SensitiveField>
                  </template>
                </BAutocomplete>
              </BField>
            </ValidationProvider>
          </section>

          <section v-if="publicChannelPartners?.length" class="mt-4">
            <h4 class="title is-4">Trade Organisations</h4>
            <div class="columns is-multiline">
              <div v-for="{ id, name } in publicChannelPartners" :key="id" class="column is-6">
                <BCheckbox v-model="currentChannelPartners" :native-value="id" :data-cy-tag="`channel-partner-${name}`">
                  <SensitiveFields>{{ name }}</SensitiveFields>
                </BCheckbox>
              </div>
            </div>
          </section>

          <div v-if="requireTermsAcceptance" class="pt-2">
            <ValidationProvider name="otherTerms" slim>
              <BField data-cy-tag="terms-acceptance">
                <BCheckbox v-model="termsAcceptance">
                  I agree to Assuro's
                  <RouterLink :to="{ name: 'terms' }" target="_blank">Terms and Conditions</RouterLink>.
                </BCheckbox>
              </BField>
            </ValidationProvider>
            <ValidationProvider v-slot="{ errors }" rules="acceptTerms:@otherTerms" tag="div">
              <BField :type="{ 'is-danger': errors[0] }" :message="errors" data-cy-tag="privacy-acceptance">
                <BCheckbox v-model="privacyAcceptance">
                  I agree to Assuro's
                  <a href="https://assuro.com.au/privacy" target="_blank"> Privacy Policy </a> -
                  <a href="https://assuro.com.au/complaintspolicy" target="_blank"> Complaints Policy </a>
                </BCheckbox>
              </BField>
            </ValidationProvider>
          </div>
          <BNotification :active="isError" type="is-danger" :closable="false"
            >Sorry, we were unable to create your organisation.</BNotification
          >
        </section>
        <footer class="modal-card-foot">
          <BButton v-if="showCancelButton" @click="$emit('close')" data-cy-tag="cancel"> Cancel </BButton>
          <BButton type="is-primary" native-type="submit" data-cy-tag="submit"> Save </BButton>
        </footer>
        <BLoading :is-full-page="false" :active.sync="isLoading" />
      </div>
    </form>
  </ValidationObserver>
</template>

<script lang="ts">
import { INDUSTRY_OPTIONS, ORGANISATION_VARIANT_OPTIONS, PARTY_ENTITY_TYPE_OPTIONS, UPDATE_DETAILS } from '@/constants'
import { createOrganisation, fetchOrganisation, mutations, profileTermsAccepted, queries } from '@/graphql'
import {
  ChannelPartners,
  CreateOrganisationInput,
  Entity,
  Organisation,
  OrganisationCapability,
  PartyEntityType,
  Profile,
  SortEnumType,
  UpdateOrganisationInput,
  UpdateOrganisationPayload
} from '@/graphql/types'
import {
  ALL_ORGANISATIONS,
  CLOSE_INIT_PROMPT,
  FETCH_USER_STATE,
  PROFILE,
  RELOAD_ORGANISATIONS,
  SET_ORGANISATION_ID,
  USER_MOD
} from '@/store'
import { displayErrors, namespaceWrapper, trackEvent } from '@/utils'
import { clone, isEmpty } from 'lodash'
import get from 'lodash/get'
import { v4 as uuid } from 'uuid'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { SensitiveField } from '~/components'
import { INT32_MAX } from '~/constants/numbers'
import { sensitiveField } from '~/utils'

const user = namespaceWrapper(USER_MOD)
const EMPTY_ORGANISATION: Partial<Organisation> = {
  id: '',
  name: '',
  address: '',
  postalAddress: '',
  postalAddressSameAsBusinessAddress: false,
  entityType: undefined
}
@Component({
  components: { ValidationProvider, ValidationObserver, SensitiveField },
  methods: {
    sensitiveField
  }
})
export default class OrganisationCreateAndUpdate extends Vue {
  @Prop({ default: false }) showCancelButton: boolean
  @Prop({ required: true }) requireTermsAcceptance: boolean
  @Prop() organisationId: string

  industryOptions = INDUSTRY_OPTIONS
  organisationOptions = ORGANISATION_VARIANT_OPTIONS
  partyEntityTypeOptions = PARTY_ENTITY_TYPE_OPTIONS

  termsAcceptance: boolean = false
  privacyAcceptance: boolean = false
  publicChannelPartners: ChannelPartners[] = []
  isLoading: boolean = false
  isError: boolean = false
  isReferredToAssuro: boolean = false
  entityList: Entity[] = []
  searchByName: string = ''
  capabilities: OrganisationCapability[] = []

  //organisation details
  orgName: string = ''
  abn: string | null = null
  industryType: string | null = null
  address: string = ''
  entityType: PartyEntityType | null = null
  selectedEntity: Entity | null = null
  currentChannelPartners: string[] = []
  organisationEdits: Partial<Organisation> = EMPTY_ORGANISATION

  get header(): string {
    return this.organisationId ? 'Update Organisation' : 'Create Organisation'
  }

  get filteredEntities() {
    const searchValue = this.searchByName.toLowerCase()
    return this.entityList.filter(option => {
      const searchOption = option.name.toLowerCase()
      return searchOption.indexOf(searchValue) >= 0
    })
  }

  get hasOrganisation(): boolean {
    return !isEmpty(this.$store.getters[user(ALL_ORGANISATIONS)])
  }

  get userProfile(): Profile {
    return this.$store.getters[user(PROFILE)]
  }

  get hasUserBeenReferred(): boolean {
    return !!this.userProfile.referrerCode || !!this.userProfile.referralPortalReferrerMemberCode
  }

  get isReferredSectionVisible(): boolean {
    return !this.hasOrganisation && !this.hasUserBeenReferred
  }

  get cyTag(): string {
    if (this.requireTermsAcceptance) {
      return 'organisation-create-and-terms-acceptance'
    }
    return 'organisation-create'
  }

  get userCanUpdateDetails() {
    return this.capabilities.includes(UPDATE_DETAILS)
  }

  async mounted() {
    this.setOrganisationDetails()
    await this.getPublicChannelPartners()
    await this.fetchPartners()
    if (
      this.userProfile &&
      this.userProfile.industry &&
      this.industryType === null &&
      this.industryOptions.has(this.userProfile.industry)
    ) {
      this.industryType = this.userProfile.industry
    }
  }

  async fetchOrganisation() {
    if (!this.organisationId) {
      return
    }
    this.isLoading = true
    await fetchOrganisation(this.organisationId)
      .then(org => {
        this.organisationEdits = clone(org)
        this.capabilities = org.capabilities || []
        this.organisationEdits?.channelPartnerOrganisations?.nodes
          ? this.organisationEdits?.channelPartnerOrganisations?.nodes?.forEach(node => {
              this.currentChannelPartners?.push(node?.channelPartner?.id!)
            })
          : []
      })
      .catch((error: any) =>
        this.$buefy.toast.open({
          message: error.message,
          type: 'is-danger'
        })
      )
      .finally(() => (this.isLoading = false))
  }

  async setOrganisationDetails() {
    if (!this.organisationId) return
    await this.fetchOrganisation()
    this.address = this.organisationEdits.address ?? ''
    this.abn = this.organisationEdits.abn ?? ''
    this.entityType = this.organisationEdits.entityType ?? null
    this.orgName = this.organisationEdits.name ?? ''
    this.industryType = this.organisationEdits.industryType ?? this.userProfile.industry ?? ''
    this.organisationEdits?.channelPartnerOrganisations?.nodes
      ? this.organisationEdits?.channelPartnerOrganisations?.nodes?.forEach(node => {
          this.currentChannelPartners?.push(node?.channelPartner?.id!)
        })
      : []
  }

  selectUpdateOrCreate() {
    if (!this.organisationId) {
      this.createOrganisation()
    }
    this.onUpdate()
  }

  async getPublicChannelPartners() {
    await this.$apollo
      .query({
        query: queries.CHANNEL_PARTNERS_ACTIVE_QUERY,
        variables: {
          where: {
            isPublic: {
              eq: true
            }
          }
        }
      })
      .then(response => (this.publicChannelPartners = get(response, 'data.channelPartners.nodes')))
      .catch(errors => {
        displayErrors(errors)
      })
  }

  close() {
    this.$emit('close')
    this.$store.commit(user(CLOSE_INIT_PROMPT))
  }

  onUpdate() {
    this.isLoading = true
    if (!this.organisationEdits.id || !this.orgName) return
    const input: UpdateOrganisationInput = {
      clientMutationId: uuid(),
      id: this.organisationEdits.id,
      name: this.orgName!,
      abn: this.abn!,
      address: this.address,
      entityType: this.entityType!,
      channelPartnerIds: [...new Set(this.currentChannelPartners)]
    }
    this.$apollo
      .mutate<UpdateOrganisationPayload, { input: UpdateOrganisationInput }>({
        mutation: mutations.UPDATE_ORGANISATION_MUTATION,
        variables: {
          input
        }
      })
      .then(() => {
        trackEvent('ORGANISATION_UPDATED', {
          organisationId: this.organisationEdits.id,
          organisationName: name
        })
        this.$buefy.toast.open({
          message: 'Organisation Details Updated'
        })
        this.close()
      })
      .then(() => this.$store.dispatch(user(RELOAD_ORGANISATIONS)))
      .catch(errors => {
        displayErrors(errors)
      })
      .finally(() => {
        this.currentChannelPartners = []
        this.isLoading = false
      })
  }

  async createOrganisation() {
    this.isLoading = true
    const payload: CreateOrganisationInput = {
      clientMutationId: uuid(),
      abn: !this.abn ? undefined : this.abn,
      address: this.address,
      industryType: this.industryType ? this.industryType : undefined,
      name: this.orgName,
      referralPortalReferralEntityId: this.selectedEntity ? this.selectedEntity.id : undefined,
      channelPartnerIds: this.currentChannelPartners,
      entityType: this.entityType!
    }
    if (this.requireTermsAcceptance) {
      await profileTermsAccepted({
        clientMutationId: uuid()
      }).catch(errors => {
        this.isError = true
        displayErrors(errors)
      })
    }

    createOrganisation(payload)
      .then(
        ({
          data: {
            createOrganisation: { id }
          }
        }) => {
          trackEvent('ORGANISATION_CREATED', {
            organisationId: id,
            organisationName: this.orgName
          })

          this.$store.dispatch(user(FETCH_USER_STATE)).then(() => this.$store.commit(user(SET_ORGANISATION_ID), id))
          this.close()
          this.$buefy.toast.open({
            message: 'Organisation successfully created'
          })
        }
      )
      .catch(errors => {
        displayErrors(errors)
      })
      .finally(() => (this.isLoading = false))
  }

  async fetchPartners() {
    this.isLoading = true
    const variables = {
      order: [{ entityType: SortEnumType.ASC, name: SortEnumType.ASC }],
      first: INT32_MAX // set pagesize - int.maxValue
    }
    await this.$apollo
      .query({
        query: queries.GET_REFERRAL_PORTAL_ENTITIES,
        variables
      })
      .then(({ data: { referralPortalEntitiesForClientPortal } }) => {
        this.entityList = referralPortalEntitiesForClientPortal.nodes
      })
      .catch(errors => {
        displayErrors(errors)
      })
      .finally(() => (this.isLoading = false))
  }
}
</script>
<style lang="scss" scoped></style>
